import type Konva from "konva";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { Arrow, Circle, Group, Line, Rect, Text, Transformer } from "react-konva";
import type { DraftElement, Shape, Stroke, TextAnnotation } from "./types.js";

interface DrawingLayerProps {
  strokes: readonly Stroke[];
  shapes: readonly Shape[];
  texts: readonly TextAnnotation[];
  /** Live preview draft drawn by the active tool. */
  draft?: DraftElement | null;
  selectedIds: ReadonlySet<string>;
  /** Allow drag-to-move + resize handles when in select mode. */
  editable: boolean;
  onSelectElement: (id: string) => void;
  /** Fired when a shape drag gesture starts — opt-in hook used by editors
   * that support multi-selection group drag (e.g. whiteboard). */
  onShapeDragStart?: (id: string, x: number, y: number) => void;
  onMoveShape: (id: string, x: number, y: number) => void;
  /** Live (per-frame) shape translation while a drag is in flight. */
  onMoveShapeLive: (id: string, x: number, y: number) => void;
  onResizeShape: (id: string, patch: { x?: number; y?: number; w?: number; h?: number }) => void;
  /** Live (per-frame) shape resize while a handle drag is in flight. */
  onResizeShapeLive: (
    id: string,
    patch: { x?: number; y?: number; w?: number; h?: number },
  ) => void;
  /** Fired when a text drag gesture starts (group-drag hook). */
  onTextDragStart?: (id: string, x: number, y: number) => void;
  /** Optional per-frame text drag callback (group-drag hook). */
  onMoveTextLive?: (id: string, x: number, y: number) => void;
  onMoveText: (id: string, x: number, y: number) => void;
  /**
   * Opt-in: when provided, a single selected text gets corner resize handles
   * that scale its font size. Called once per gesture with the new font size
   * and top-left position so the caller can commit a single undo step.
   */
  onResizeText?: (id: string, patch: { x: number; y: number; fontSize: number }) => void;
  onTextDblClick: (id: string) => void;
}

const HANDLE_FILL = "#97fc00";
const HANDLE_STROKE = "#1d3700";
const HANDLE_R = 14;
/** Font-size range accepted from the text resize handles. */
const TEXT_FONT_MIN = 10;
const TEXT_FONT_MAX = 400;

/**
 * Renders strokes, shapes and texts on a dedicated Konva layer. Shapes and
 * texts are draggable when `editable` and selected; exactly-one shape
 * selection also reveals endpoint / corner / radius resize handles.
 *
 * Pure rendering — the canvas-kit layer does not know anything about the
 * caller's domain model (Scene, Frame, …); inputs are plain arrays of
 * primitives so any feature (Tactics, Whiteboard, …) can consume it.
 */
export function DrawingLayer({
  strokes,
  shapes,
  texts,
  draft,
  selectedIds,
  editable,
  onSelectElement,
  onShapeDragStart,
  onMoveShape,
  onMoveShapeLive,
  onResizeShape,
  onResizeShapeLive,
  onTextDragStart,
  onMoveTextLive,
  onMoveText,
  onResizeText,
  onTextDblClick,
}: DrawingLayerProps) {
  const singleShapeId = editable && selectedIds.size === 1 ? [...selectedIds][0]! : null;
  const handleShape = singleShapeId ? (shapes.find((s) => s.id === singleShapeId) ?? null) : null;
  const handleText =
    singleShapeId && onResizeText ? (texts.find((t) => t.id === singleShapeId) ?? null) : null;

  // Text resize: a Konva Transformer (same lime look as the token handles)
  // attached to the single selected text node. The gesture scales the node;
  // on transformend the scale is folded into `fontSize` and reset to 1.
  const textNodes = useRef<Map<string, Konva.Text>>(new Map());
  const registerTextNode = useCallback((id: string, node: Konva.Text | null) => {
    if (node) textNodes.current.set(id, node);
    else textNodes.current.delete(id);
  }, []);
  const textTrRef = useRef<Konva.Transformer | null>(null);
  useEffect(() => {
    const tr = textTrRef.current;
    if (!tr) return;
    const node = handleText ? textNodes.current.get(handleText.id) : undefined;
    tr.nodes(node ? [node] : []);
    // Re-measure after value / fontSize changes so the box hugs the text.
    tr.forceUpdate();
    tr.getLayer()?.batchDraw();
  }, [handleText]);
  const commitTextResize = useCallback(() => {
    const tr = textTrRef.current;
    const node = tr?.nodes()[0];
    if (!node || !handleText || !onResizeText) return;
    const mul = (Math.abs(node.scaleX()) + Math.abs(node.scaleY())) / 2;
    node.scaleX(1);
    node.scaleY(1);
    if (!Number.isFinite(mul) || mul <= 0) return;
    const fontSize = Math.round(
      Math.min(TEXT_FONT_MAX, Math.max(TEXT_FONT_MIN, handleText.fontSize * mul)),
    );
    onResizeText(handleText.id, { x: node.x(), y: node.y(), fontSize });
  }, [handleText, onResizeText]);

  // Hide resize handles while a shape is actively being dragged. Konva moves
  // the shape's Group directly on the GPU during drag, but the handles re-
  // render from the React-side store snapshot on every onDragMove → they
  // visually trail the shape by 1-2 frames. We just don't render them mid-
  // drag (they have no function during a translation gesture anyway).
  const [draggingId, setDraggingId] = useState<string | null>(null);

  return (
    <>
      {strokes.map((st) => (
        <StrokeNode
          key={st.id}
          stroke={st}
          selected={selectedIds.has(st.id)}
          onSelect={() => onSelectElement(st.id)}
        />
      ))}
      {shapes.map((sh) => (
        <ShapeNode
          key={sh.id}
          id={sh.id}
          shape={sh}
          selected={selectedIds.has(sh.id)}
          draggable={editable && selectedIds.has(sh.id)}
          onSelect={() => onSelectElement(sh.id)}
          onDragStart={(x, y) => {
            setDraggingId(sh.id);
            onShapeDragStart?.(sh.id, x, y);
          }}
          onMove={(x, y) => {
            setDraggingId(null);
            onMoveShape(sh.id, x, y);
          }}
          onMoveLive={(x, y) => onMoveShapeLive(sh.id, x, y)}
        />
      ))}
      {texts.map((tx) => (
        <TextNode
          key={tx.id}
          id={tx.id}
          text={tx}
          selected={selectedIds.has(tx.id)}
          draggable={editable && selectedIds.has(tx.id)}
          onSelect={() => onSelectElement(tx.id)}
          onDragStart={onTextDragStart ? (x, y) => onTextDragStart(tx.id, x, y) : undefined}
          onMoveLive={onMoveTextLive ? (x, y) => onMoveTextLive(tx.id, x, y) : undefined}
          onMove={(x, y) => onMoveText(tx.id, x, y)}
          onDblClick={() => onTextDblClick(tx.id)}
          nodeRef={onResizeText ? (node) => registerTextNode(tx.id, node) : undefined}
        />
      ))}
      {/* draft preview (uncommitted) */}
      {draft?.kind === "stroke" && (
        <StrokeNode stroke={draft.stroke} selected={false} onSelect={noop} />
      )}
      {draft?.kind === "shape" && (
        <ShapeNode
          shape={draft.shape}
          selected={false}
          draggable={false}
          onSelect={noop}
          onMove={noop2}
          onMoveLive={noop2}
        />
      )}
      {/* resize handles for the single selected shape — hidden during drag
          so they don't visually trail the GPU-moved shape. */}
      {handleShape && draggingId !== handleShape.id && (
        <ResizeHandles
          shape={handleShape}
          onResize={(patch) => onResizeShape(handleShape.id, patch)}
          onResizeLive={(patch) => onResizeShapeLive(handleShape.id, patch)}
        />
      )}
      {onResizeText && (
        <Transformer
          ref={(node) => {
            textTrRef.current = node;
          }}
          rotateEnabled={false}
          flipEnabled={false}
          keepRatio
          ignoreStroke
          enabledAnchors={["top-left", "top-right", "bottom-left", "bottom-right"]}
          anchorSize={12}
          anchorStroke="#97fc00"
          anchorFill="#0b1220"
          anchorCornerRadius={3}
          borderStroke="#97fc00"
          borderStrokeWidth={1.5}
          borderDash={[4, 4]}
          // Stop shrinking once the glyphs would drop below the minimum size.
          boundBoxFunc={(oldBox, newBox) => {
            if (newBox.height < TEXT_FONT_MIN || newBox.width < TEXT_FONT_MIN) return oldBox;
            return newBox;
          }}
          onTransformEnd={commitTextResize}
        />
      )}
    </>
  );
}

function noop() {}
function noop2(_x: number, _y: number) {}

function StrokeNode({
  stroke,
  selected,
  onSelect,
}: {
  stroke: Stroke;
  selected: boolean;
  onSelect: () => void;
}) {
  const tap = (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
    e.cancelBubble = true;
    if (!selected) onSelect();
  };
  return (
    <Line
      points={stroke.points}
      stroke={stroke.color}
      strokeWidth={selected ? stroke.width + 3 : stroke.width}
      tension={0.35}
      lineCap="round"
      lineJoin="round"
      dash={stroke.dashed ? [stroke.width * 3, stroke.width * 2] : undefined}
      hitStrokeWidth={Math.max(stroke.width * 2, 24)}
      onMouseDown={tap}
      onTouchStart={tap}
    />
  );
}

function ShapeNode({
  id,
  shape,
  selected,
  draggable,
  onSelect,
  onDragStart,
  onMove,
  onMoveLive,
}: {
  /** Optional Konva node id, used by callers that need `stage.findOne()` for
   * group-drag (e.g. whiteboard). Pure tactics callers can omit. */
  id?: string;
  shape: Shape;
  selected: boolean;
  draggable: boolean;
  onSelect: () => void;
  onDragStart?: (x: number, y: number) => void;
  onMove: (x: number, y: number) => void;
  onMoveLive: (x: number, y: number) => void;
}) {
  const tap = (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
    e.cancelBubble = true;
    if (!selected) onSelect();
  };
  const dragStart = (e: Konva.KonvaEventObject<DragEvent>) => {
    e.cancelBubble = true;
    onDragStart?.(e.target.x(), e.target.y());
  };
  const dragMove = (e: Konva.KonvaEventObject<DragEvent>) => {
    e.cancelBubble = true;
    onMoveLive(e.target.x(), e.target.y());
  };
  const dragEnd = (e: Konva.KonvaEventObject<DragEvent>) => {
    e.cancelBubble = true;
    onMove(e.target.x(), e.target.y());
  };
  const stroke = shape.color;
  const dash = shape.dashed ? [shape.width * 3, shape.width * 2] : undefined;
  const sw = selected ? shape.width + 3 : shape.width;

  // Lines and arrows live as a Group at (x,y) with points relative to that
  // group so dragging the Group cleanly translates the whole element.
  if (shape.kind === "line" || shape.kind === "arrow") {
    const pts = [0, 0, shape.w, shape.h];
    const inner =
      shape.kind === "arrow" ? (
        <Arrow
          points={pts}
          stroke={stroke}
          fill={stroke}
          strokeWidth={sw}
          dash={dash}
          pointerLength={shape.width * 4}
          pointerWidth={shape.width * 4}
          hitStrokeWidth={Math.max(shape.width * 2, 24)}
        />
      ) : (
        <Line
          points={pts}
          stroke={stroke}
          strokeWidth={sw}
          dash={dash}
          lineCap="round"
          hitStrokeWidth={Math.max(shape.width * 2, 24)}
        />
      );
    return (
      <Group
        {...(id ? { id: `wb-shape-${id}` } : {})}
        x={shape.x}
        y={shape.y}
        draggable={draggable}
        onMouseDown={tap}
        onTouchStart={tap}
        onDragStart={dragStart}
        onDragMove={dragMove}
        onDragEnd={dragEnd}
      >
        {inner}
      </Group>
    );
  }
  if (shape.kind === "rect") {
    return (
      <Rect
        {...(id ? { id: `wb-shape-${id}` } : {})}
        x={shape.x}
        y={shape.y}
        width={shape.w}
        height={shape.h}
        stroke={stroke}
        strokeWidth={sw}
        dash={dash}
        cornerRadius={6}
        // Hollow interior: only the stroke is hit-testable so tokens and
        // other elements can be placed inside the shape without grabbing it.
        hitStrokeWidth={Math.max(sw * 2, 18)}
        hitFunc={(ctx, node) => {
          const w = (node as Konva.Rect).width();
          const h = (node as Konva.Rect).height();
          ctx.beginPath();
          // Keep cornerRadius in sync with the rendered shape.
          const r = 6;
          ctx.moveTo(r, 0);
          ctx.lineTo(w - r, 0);
          ctx.quadraticCurveTo(w, 0, w, r);
          ctx.lineTo(w, h - r);
          ctx.quadraticCurveTo(w, h, w - r, h);
          ctx.lineTo(r, h);
          ctx.quadraticCurveTo(0, h, 0, h - r);
          ctx.lineTo(0, r);
          ctx.quadraticCurveTo(0, 0, r, 0);
          ctx.closePath();
          ctx.strokeShape(node);
        }}
        draggable={draggable}
        onMouseDown={tap}
        onTouchStart={tap}
        onDragStart={dragStart}
        onDragMove={dragMove}
        onDragEnd={dragEnd}
      />
    );
  }
  // circle: x/y center, w radius
  return (
    <Circle
      {...(id ? { id: `wb-shape-${id}` } : {})}
      x={shape.x}
      y={shape.y}
      radius={Math.max(Math.abs(shape.w), 1)}
      stroke={stroke}
      strokeWidth={sw}
      dash={dash}
      // Hollow interior: only the ring is hit-testable.
      hitStrokeWidth={Math.max(sw * 2, 18)}
      hitFunc={(ctx, node) => {
        const r = (node as Konva.Circle).radius();
        ctx.beginPath();
        ctx.arc(0, 0, r, 0, Math.PI * 2, false);
        ctx.closePath();
        ctx.strokeShape(node);
      }}
      draggable={draggable}
      onMouseDown={tap}
      onTouchStart={tap}
      onDragStart={dragStart}
      onDragMove={dragMove}
      onDragEnd={dragEnd}
    />
  );
}

function TextNode({
  id,
  text,
  selected,
  draggable,
  onSelect,
  onDragStart,
  onMoveLive,
  onMove,
  onDblClick,
  nodeRef,
}: {
  id?: string;
  text: TextAnnotation;
  selected: boolean;
  draggable: boolean;
  onSelect: () => void;
  onDragStart?: (x: number, y: number) => void;
  onMoveLive?: (x: number, y: number) => void;
  onMove: (x: number, y: number) => void;
  onDblClick: () => void;
  nodeRef?: (node: Konva.Text | null) => void;
}) {
  return (
    <Text
      {...(id ? { id: `wb-text-${id}` } : {})}
      {...(nodeRef ? { ref: nodeRef } : {})}
      x={text.x}
      y={text.y}
      text={text.value}
      fontSize={text.fontSize}
      fontStyle="700"
      fill={text.color}
      shadowColor="black"
      shadowBlur={6}
      shadowOpacity={0.5}
      draggable={draggable}
      onMouseDown={(e) => {
        e.cancelBubble = true;
        if (!selected) onSelect();
      }}
      onTouchStart={(e) => {
        e.cancelBubble = true;
        if (!selected) onSelect();
      }}
      onDragStart={(e) => {
        e.cancelBubble = true;
        onDragStart?.(e.target.x(), e.target.y());
      }}
      onDragMove={(e) => {
        if (!onMoveLive) return;
        e.cancelBubble = true;
        onMoveLive(e.target.x(), e.target.y());
      }}
      onDragEnd={(e) => {
        e.cancelBubble = true;
        onMove(e.target.x(), e.target.y());
      }}
      onDblClick={onDblClick}
      onDblTap={onDblClick}
      stroke={selected ? "#97fc00" : undefined}
      strokeWidth={selected ? 0.75 : 0}
    />
  );
}

/**
 * Resize handles for the single selected shape.
 *
 *  - line/arrow: 2 endpoint handles at (x,y) and (x+w, y+h)
 *  - rect: 1 bottom-right handle at (x+w, y+h)
 *  - circle: 1 east-edge handle at (x+w, y)  (w is the radius)
 *
 * Each handle is a draggable Konva.Circle. During the drag we dispatch
 * transient patches via `onResizeLive` so the shape geometry follows the
 * pointer in real time; the final value is committed with `onResize` on
 * dragEnd so undo treats the whole gesture as one history entry.
 */
function ResizeHandles({
  shape,
  onResize,
  onResizeLive,
}: {
  shape: Shape;
  onResize: (patch: { x?: number; y?: number; w?: number; h?: number }) => void;
  onResizeLive: (patch: { x?: number; y?: number; w?: number; h?: number }) => void;
}) {
  if (shape.kind === "line" || shape.kind === "arrow") {
    return (
      <Fragment>
        <Handle
          x={shape.x}
          y={shape.y}
          onDragMove={(nx, ny) => {
            const endX = shape.x + shape.w;
            const endY = shape.y + shape.h;
            onResizeLive({ x: nx, y: ny, w: endX - nx, h: endY - ny });
          }}
          onDragEnd={(nx, ny) => {
            const endX = shape.x + shape.w;
            const endY = shape.y + shape.h;
            onResize({ x: nx, y: ny, w: endX - nx, h: endY - ny });
          }}
        />
        <Handle
          x={shape.x + shape.w}
          y={shape.y + shape.h}
          onDragMove={(nx, ny) => {
            onResizeLive({ w: nx - shape.x, h: ny - shape.y });
          }}
          onDragEnd={(nx, ny) => {
            onResize({ w: nx - shape.x, h: ny - shape.y });
          }}
        />
      </Fragment>
    );
  }
  if (shape.kind === "rect") {
    return (
      <Handle
        x={shape.x + shape.w}
        y={shape.y + shape.h}
        onDragMove={(nx, ny) => {
          onResizeLive({
            w: Math.max(20, nx - shape.x),
            h: Math.max(20, ny - shape.y),
          });
        }}
        onDragEnd={(nx, ny) => {
          onResize({
            w: Math.max(20, nx - shape.x),
            h: Math.max(20, ny - shape.y),
          });
        }}
      />
    );
  }
  // circle: handle on the east edge; radius = distance from center
  return (
    <Handle
      x={shape.x + Math.abs(shape.w)}
      y={shape.y}
      onDragMove={(nx, ny) => {
        const r = Math.max(15, Math.hypot(nx - shape.x, ny - shape.y));
        onResizeLive({ w: r });
      }}
      onDragEnd={(nx, ny) => {
        const r = Math.max(15, Math.hypot(nx - shape.x, ny - shape.y));
        onResize({ w: r });
      }}
    />
  );
}

function Handle({
  x,
  y,
  onDragMove,
  onDragEnd,
}: {
  x: number;
  y: number;
  onDragMove: (x: number, y: number) => void;
  onDragEnd: (x: number, y: number) => void;
}) {
  return (
    <Circle
      x={x}
      y={y}
      radius={HANDLE_R}
      fill={HANDLE_FILL}
      stroke={HANDLE_STROKE}
      strokeWidth={3}
      shadowColor="black"
      shadowBlur={6}
      shadowOpacity={0.4}
      draggable
      onMouseDown={(e) => {
        e.cancelBubble = true;
      }}
      onTouchStart={(e) => {
        e.cancelBubble = true;
      }}
      onDragMove={(e) => {
        e.cancelBubble = true;
        onDragMove(e.target.x(), e.target.y());
      }}
      onDragEnd={(e) => {
        e.cancelBubble = true;
        onDragEnd(e.target.x(), e.target.y());
      }}
    />
  );
}
